<script setup lang="ts">
import DocumentsSection from '@/components/documents/DocumentsSection.vue'
import SectionTabs, { type SectionTab } from '@/components/SectionTabs.vue'
import PropertyBuildingTab from '@/pages/investors/portfolio/PropertyBuildingTab.vue'
import PropertyModernizationTab from '@/pages/investors/portfolio/PropertyModernizationTab.vue'
import PropertyOverviewTab from '@/pages/investors/portfolio/PropertyOverviewTab.vue'
import PropertyPurchaseTab from '@/pages/investors/portfolio/PropertyPurchaseTab.vue'
import PropertyRentTab from '@/pages/investors/portfolio/PropertyRentTab.vue'
import { missingPerPortfolioTab, portfolioDataTabs, type PortfolioTab } from '@/pages/investors/portfolio/tabs'
import type { ExtendedEnum, PersonGroup } from '@/types'
import type { FieldStateMap } from '@/utils/field-state'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import AttachmentData = App.Data.AttachmentData
import DocumentSectionData = App.Data.Documents.DocumentSectionData
import DocumentTypeOptionData = App.Data.Documents.DocumentTypeOptionData
import EstateObjectData = App.Data.EstateObject.EstateObjectData
import LoanData = App.Data.EstateObject.LoanData
import ModernizationData = App.Data.EstateObject.ModernizationData
import RentalUnitData = App.Data.EstateObject.RentalUnitData
import ElevatorType = App.Enums.EstateObject.ElevatorType
import EnergyCertificateType = App.Enums.EstateObject.EnergyCertificateType
import EnergySourceType = App.Enums.EstateObject.EnergySourceType
import EstateObjectType = App.Enums.EstateObject.EstateObjectType
import HeatingType = App.Enums.EstateObject.HeatingType
import RoofType = App.Enums.EstateObject.RoofType
import WindowType = App.Enums.EstateObject.WindowType
import CollateralType = App.Enums.EstateObject.Loans.CollateralType
import FinancingType = App.Enums.EstateObject.Loans.FinancingType
import MezzanineType = App.Enums.EstateObject.Loans.MezzanineType

const props = defineProps<{
  estateObject: EstateObjectData
  fieldStates: FieldStateMap
  photos: AttachmentData[]
  documents: DocumentSectionData
  documentTypeOptions: DocumentTypeOptionData[]
  loans: LoanData[]
  rentalUnits: RentalUnitData[]
  modernizations: ModernizationData[]
  estateObjectTypes: ExtendedEnum<EstateObjectType>[]
  heatingTypes: ExtendedEnum<HeatingType>[]
  energySourceTypes: ExtendedEnum<EnergySourceType>[]
  energyCertificateTypes: ExtendedEnum<EnergyCertificateType>[]
  elevatorTypes: ExtendedEnum<ElevatorType>[]
  roofTypes: ExtendedEnum<RoofType>[]
  windowTypes: ExtendedEnum<WindowType>[]
  financingTypes: ExtendedEnum<FinancingType>[]
  mezzanineTypes: ExtendedEnum<MezzanineType>[]
  collateralTypes: ExtendedEnum<CollateralType>[]
  improvementTypes: string[]
  landRegisterChargeDefaults: Record<string, { label: string; rate: number }>
  personOptions: PersonGroup[]
  ariaLabel?: string
}>()

const activeTab = defineModel<PortfolioTab>('tab', { required: true })

const { t } = useI18n()

const missingPerTab = computed(() => missingPerPortfolioTab(props.fieldStates))

const tabs = computed<SectionTab<PortfolioTab>[]>(() => [
  ...portfolioDataTabs.map((tab) => ({
    label: t(`tabs.${tab}`),
    value: tab,
    count: missingPerTab.value[tab],
  })),
  { label: t('tabs.documents'), value: 'documents' as const, icon: 'pi pi-file', trailing: true },
])
</script>

<template>
  <div>
    <SectionTabs v-model="activeTab" :tabs="tabs" :aria-label="ariaLabel" />

    <PropertyOverviewTab
      v-if="activeTab === 'overview'"
      :estate-object="estateObject"
      :field-states="fieldStates"
      :estate-object-types="estateObjectTypes"
      :land-register-charge-defaults="landRegisterChargeDefaults"
      :person-options="personOptions"
      :photos="photos" />

    <PropertyPurchaseTab
      v-else-if="activeTab === 'purchase'"
      :estate-object="estateObject"
      :field-states="fieldStates"
      :loans="loans"
      :modernizations="modernizations"
      :financing-types="financingTypes"
      :mezzanine-types="mezzanineTypes"
      :collateral-types="collateralTypes"
      @open-modernizations="activeTab = 'modernization'" />

    <PropertyBuildingTab
      v-else-if="activeTab === 'building'"
      :estate-object="estateObject"
      :field-states="fieldStates"
      :roof-types="roofTypes"
      :window-types="windowTypes"
      :elevator-types="elevatorTypes"
      :heating-types="heatingTypes"
      :energy-source-types="energySourceTypes"
      :energy-certificate-types="energyCertificateTypes" />

    <PropertyRentTab
      v-else-if="activeTab === 'rent'"
      :estate-object="estateObject"
      :field-states="fieldStates"
      :rental-units="rentalUnits" />

    <PropertyModernizationTab
      v-else-if="activeTab === 'modernization'"
      :estate-object="estateObject"
      :modernizations="modernizations"
      :improvement-types="improvementTypes" />

    <DocumentsSection v-else :section="documents" :type-options="documentTypeOptions" />
  </div>
</template>

<i18n>
{
  "de": {
    "tabs": {
      "overview": "Überblick",
      "purchase": "Kauf & Finanzierung",
      "building": "Gebäude & Ausstattung",
      "rent": "Mieten & Einheiten",
      "modernization": "Modernisierungen",
      "documents": "Dokumente"
    }
  },
  "en": {
    "tabs": {
      "overview": "Overview",
      "purchase": "Purchase & financing",
      "building": "Building & fittings",
      "rent": "Rents & units",
      "modernization": "Modernisations",
      "documents": "Documents"
    }
  }
}
</i18n>
